import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { useLazyGenerateMessagesOldQuery, useLazyGetMessageTemplatesOldQuery } from "store/api/contractorsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useGetDevicesQuery } from "store/api/devicesApi"
import { useAppSelector } from "store/hooks"
import { deviceSystemAgeOptions } from "constants/DeviceSystemAgeOptions"
import {
  deviceAccessoriesOptions,
  deviceSystemTypeOptions,
  hvacOptions,
  optInOptions,
} from "constants/SelectFieldOptions"
import { ApiTypes, UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { convertDevicesToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { Spinner } from "components/common/Spinner"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import { SelectDynamic } from "components/form/SelectDynamic"
import { CustomerEmail } from "components/table/columnComponents/CustomerEmail"
import { CustomerMembershipType } from "components/table/columnComponents/CustomerMembershipType"
import { OptedInOut } from "components/table/columnComponents/OptedInOut"
import TableWrapper from "components/table/TableWrapper"

import { dummyDevices, dummyMeta } from "../MonitoringPage/dummyMonitoringData"

import styles from "./index.module.scss"

const initialTableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "",
    name: "checkbox",
    type: "checkbox",
    size: "mini",
    cellComponent: "checkbox",
  },
  {
    title: "Email",
    type: "sort-search",
    name: "email",
    size: "middle",
    cellComponent: CustomerEmail,
  },
  {
    title: "Device S/N",
    type: "sort-search",
    name: "sn",
    size: "small",
  },
  {
    title: "Country",
    type: "sort-search",
    name: "country",
    size: "small",
  },
  {
    title: "City",
    type: "sort-search",
    name: "city",
    size: "small",
  },
  {
    title: "Zip code",
    type: "sort-search",
    name: "zip_code",
    size: "small",
  },
  {
    title: "State",
    type: "sort-search",
    name: "state",
    size: "small",
  },
  {
    title: "Opt in/out",
    type: "select",
    name: "option",
    size: "small",
    options: optInOptions,
    cellComponent: OptedInOut,
  },
  {
    title: "Activation date",
    type: "sort-date-range",
    name: "activation_date",
    size: "middle",
  },
  {
    title: "Customer name",
    type: "sort-search",
    name: "homeowner_name",
  },
  {
    title: "Membership",
    type: "sort-search",
    name: "membership",
    size: "small",
  },
  {
    title: "Membership type",
    type: "search",
    name: "membership_name",
    size: "middle",
    cellComponent: CustomerMembershipType,
  },
  {
    title: "Residence type",
    type: "sort-search",
    name: "residence_type",
    size: "middle",
  },
  {
    title: "System type",
    type: "select",
    size: "small",
    name: "system_type",
    options: deviceSystemTypeOptions,
  },
  {
    title: "System age",
    type: "select",
    size: "middle",
    name: "system_age",
    options: deviceSystemAgeOptions,
  },
  {
    title: "Accessories",
    type: "select",
    name: "accessories",
    options: deviceAccessoriesOptions,
    size: "small",
  },
  {
    title: "Installation type",
    type: "select",
    size: "small",
    name: "installation_type",
    options: hvacOptions,
  },
]

export const MessageCenterOld = () => {
  const navigate = useNavigate()
  const { urlQueryParams } = useUrlQueryParams()
  const { data, isFetching } = useGetDevicesQuery(
    { ...urlQueryParams, phisical_status: "installed" },
    {
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
    },
  )

  const [triggerGenerateMessages, { isFetching: isGenerateMessagesFetching }] = useLazyGenerateMessagesOldQuery()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [templateID, setTemplateID] = useState<undefined | UITypes.Option[]>(undefined)
  const [selectedDevices, setSelectedDevices] = useState<ApiTypes.Model.Device[]>([])

  const isHeaderChecked = useMemo(() => {
    const currentPageDevices = data?.data || []
    if (!currentPageDevices.length) return false

    return currentPageDevices.every(device => selectedDevices.some(d => d.id === device.id))
  }, [data?.data, selectedDevices])
  const [isFirstRender, setFirstRender] = useState(true)
  const hasMessagesCenterAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.MessageCenter)
    : false

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(initialTableHeaderCells, columnsData?.data?.config)
        : initialTableHeaderCells,
    [columnsData],
  )

  const toggleAllDevices = () => {
    const currentPageDevices = data?.data || []
    if (isHeaderChecked) {
      const currentPageDeviceIds = currentPageDevices.map(d => d.id)
      setSelectedDevices(prev => prev.filter(d => !currentPageDeviceIds.includes(d.id)))
    } else {
      setSelectedDevices(prev => {
        const existingIds = new Set(prev.map(d => d.id))
        const toAdd = currentPageDevices.filter(d => !existingIds.has(d.id))
        return [...prev, ...toAdd]
      })
    }
  }

  const onSelectDevice = (id?: number) => {
    const devices = data?.data || []
    if (selectedDevices.some(d => d.id === id)) {
      setSelectedDevices(prev => prev.filter(d => d.id !== id))
    } else {
      const device = devices.find(d => d.id === id)
      if (device) setSelectedDevices(prev => [...prev, device])
    }
  }

  const onSendMessage = async () => {
    if (!templateID) return

    const data = {
      params: {
        message_template_id: templateID[0]?.value as number,
      },
      body: {
        device_ids: selectedDevices.map(d => d.id),
      },
    }

    try {
      const res = await triggerGenerateMessages(data)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success(
          "The messages will be sent to the selected group during 1 hour. Navigate to History page to check the status of the sent messages.",
        )
        setSelectedDevices([])
        setTemplateID(undefined)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const meta = hasMessagesCenterAccess ? data?.meta : dummyMeta

  const devices = useMemo(() => {
    if (!hasMessagesCenterAccess) {
      return dummyDevices
    }

    return data?.data || []
  }, [data, hasMessagesCenterAccess])

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    triggerGetTableColumns({ list_name: "contractor_messages_list" })
  }, [isFirstRender])

  return (
    <>
      <ContentWrapper title="Message Center">
        <nav className={styles.tableNavWrapper}>
          <div className={styles.leftNavWrapper}>
            <ButtonNew onClick={() => navigate("/messages/create-template")} color="blue">
              Create Template
            </ButtonNew>
            {hasMessagesCenterAccess && (
              <SelectDynamic
                className={styles.selectWrapper}
                valueName="id"
                labelName="title"
                useLazyQuery={useLazyGetMessageTemplatesOldQuery}
                values={templateID || []}
                onChange={setTemplateID}
              />
            )}
            {templateID?.length && selectedDevices?.length ? (
              <ButtonNew onClick={onSendMessage} color={"blue"}>
                {isGenerateMessagesFetching ? <Spinner /> : "Send"}
              </ButtonNew>
            ) : (
              <ButtonNew disabled color="grey">
                Send
              </ButtonNew>
            )}
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <EditColumnsButton tableName="contractor_messages_list" tableHeaderCells={tableHeaderCells} />
            <ButtonNew className={styles.tableNavItem} onClick={() => navigate("/messages/history")}>
              History
            </ButtonNew>
            {/* <ButtonNew className={styles.tableNavItem} disabled>Export</ButtonNew> */}
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!!columnsData}
          data={convertDevicesToTableRows(devices)}
          isLoading={isFetching}
          onCheckboxChange={onSelectDevice}
          onHeaderCheckboxChange={toggleAllDevices}
          currentItemIDs={selectedDevices.map(d => d.id)}
          isHeaderChecked={isHeaderChecked}
          additionalTags={
            selectedDevices?.length
              ? [
                  {
                    label: `Selected Device${selectedDevices.length > 1 ? "s" : ""} S/N`,
                    values: selectedDevices?.map(item => item.sn),
                  },
                ]
              : []
          }
          clearAdditionalTags={() => setSelectedDevices([])}
        />
        <Pagination meta={meta} />
      </ContentWrapper>
      {!hasMessagesCenterAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.MessageCenter} />}
    </>
  )
}
